import { useEffect, useState, useCallback, ChangeEvent, KeyboardEvent, useRef } from 'react';

import { Spinner, Box, InputGroup, InputRightElement, IconButton, Flex } from '@vimeo/bokeh';
import { ArrowUp } from '@vimeo/bokeh-icons';
import useLocalStorage from '@vimeo/core/hooks/use-storage/useLocalStorage';
import type { VideoQuestion } from '@vimeo/gctl-api/models/components/schemas/video-question/VideoQuestion';
import { Viewer, useViewer } from '@vimeo/viewer';

import { useGetSvvManageBpEvents } from '../../bp-events';
import { useQuestionContext } from '../../contexts/QuestionContext';
import { useQuestionsDataContext } from '../../contexts/QuestionsDataContext';
import * as finalCopy from '../../copy/final';
import { useBokehToast } from '../../hooks/useBokehToast';
import { useUIStore } from '../../stores/viewer-ui-store';
import { isClipInterractionsLimitError } from '../../utils';
import { sendAppReady } from '../../utils/postMessage';
import { QuestionDisplay } from '../QuestionDisplay';
import { ClipInterractionsLimitError } from './ClipInterractionsLimitError';
import { ViewerQnAPanels } from './constants';
import type { TOverrideBreakpoints, TPanels } from './constants';
import { ErrorState } from './ErrorState';
import { AiActivationPanel } from './panels/AiActivationPanel';
import { AskQuestionPanel } from './panels/AskQuestionPanel';
import { LoginRequiredPanel } from './panels/LoginRequiredPanel';
import { QuestionsPanel } from './panels/QuestionsPanel';
import { ResponsiveWrapper, LoaderWrapper, QuestionInput } from './ViewerQnA.styles';
import { ViewerQnAHeader } from './ViewerQnAHeader';

const TO_CLOSE_BRAKE_POINTS: TOverrideBreakpoints[] = ['xs', 'xxs', 'tiny', 'mini'];

interface IViewerQnA {
  onClose?: () => void;
  videoId: string;
  onMomentPlay: (timecode: number) => void;
  onLoginRequired: () => void;
  isPlayer?: boolean;
}

// Function to extract unique languages from the predefined questions
const getUniqueLanguages = (questions: VideoQuestion[]) => {
  const languagesMap = new Map<string, string>();

  questions &&
    questions.forEach((question) => {
      if (question.languageLabel && question.languageCode) {
        languagesMap.set(question.languageCode, question.languageLabel);
      }
    });

  return Array.from(languagesMap.entries()).map(([value, label]) => ({ value, label }));
};
export const ViewerQnA = ({
  onMomentPlay,
  onClose,
  videoId,
  onLoginRequired,
  isPlayer = false,
}: IViewerQnA) => {
  const overrideBreakpoint = useUIStore((state) => state.overrideBreakpoint);
  const viewer: Viewer = useViewer();
  const [activePanel, setActivePanel] = useState<TPanels>(ViewerQnAPanels.LOADER);
  const [questionText, setQuestionText] = useState('');
  const [isInterractionLimitErrorOnActivation, setIsInterractionLimitErrorOnActivation] =
    useState<boolean>(false);
  const [isInterractionLimitErrorOnAskQuestion, setIsInterractionLimitErrorOnAskQuestion] =
    useState<boolean>(false);
  const [isComposing, setIsComposing] = useState(false);

  const notify = useBokehToast();
  const { questions, error, isQuestionsLoading, isActivatingAi, forceRefresh, hasValidStatus } =
    useQuestionsDataContext();
  // uniqueLanguages will be: [{ value: 'en', label: 'English' },{ value: 'fr', label: 'Français' },{ value: 'es', label: 'Español' }]
  const uniqueLanguages = getUniqueLanguages(questions);

  const [selectedLanguage, setSelectedLanguage] = useLocalStorage(
    `ai-qna-lang`,
    viewer?.locale ?? 'en',
  );

  // filters the predefined questions by the selected language
  function filterByLanguage(questions: VideoQuestion[], language: string): VideoQuestion[] {
    const filteredQuestions =
      questions &&
      questions.filter(
        (question: VideoQuestion) =>
          question.languageCode &&
          question.languageCode.toLocaleLowerCase() === language.toLocaleLowerCase(),
      );
    return filteredQuestions?.length ? filteredQuestions : questions;
  }

  const [questionsBySelectedLang, setQuestionsBySelectedLang] = useState<VideoQuestion[]>([]);

  useEffect(
    function filterQuestionsBySelectedLang() {
      setQuestionsBySelectedLang(filterByLanguage(questions, selectedLanguage));
    },
    [questions, selectedLanguage],
  );

  const hasMultipleLanguages = uniqueLanguages.length > 1;
  const handleSelectLanguage = (language: string) => {
    if (!hasMultipleLanguages) {
      setQuestionsBySelectedLang(questions);
      return;
    }
    setSelectedLanguage(language);
    setQuestionsBySelectedLang(filterByLanguage(questions, language));
  };
  const {
    askPredefinedQuestion,
    askNewQuestion,
    questionResponseLoading,
    question,
    answer,
    questionResponseError,
  } = useQuestionContext();
  const {
    sendAskAQuestionEvent,
    sendSelectSuggestedAIQuestionEvent,
    sendViewGenerateAIAnswerEvent,
  } = useGetSvvManageBpEvents();

  const contentWrapperRef = useRef<HTMLDivElement | null>(null);

  const isActivePanelAskQuestion = activePanel === ViewerQnAPanels.ASK_QUESTION;

  const showQuestionInput =
    activePanel === ViewerQnAPanels.QUESTIONS ||
    isActivePanelAskQuestion ||
    activePanel === ViewerQnAPanels.ANSWER;

  const isQuestionInputDisabled =
    isActivePanelAskQuestion ||
    activePanel === ViewerQnAPanels.AI_ACTIVATION ||
    activePanel === ViewerQnAPanels.LOADER ||
    activePanel === ViewerQnAPanels.LOGIN_REQUIRED;

  const isEmbeded = Boolean(window?.self !== window?.top);

  const onAskPredefinedQuestion = useCallback(
    (question: VideoQuestion) => {
      if (!question.question || !question.answer) return;
      sendSelectSuggestedAIQuestionEvent({ copy: question.question, isRelated: false });
      askPredefinedQuestion(
        question.question,
        question.answer,
        question.relevantQuotes,
        question.relatedQuestions,
      );
      setActivePanel(ViewerQnAPanels.ANSWER);
    },
    [askPredefinedQuestion, sendSelectSuggestedAIQuestionEvent],
  );

  const onAskCustomQuestion = useCallback(
    (questionText: string) => {
      setQuestionText('');
      askNewQuestion(questionText);
      setActivePanel(ViewerQnAPanels.ASK_QUESTION);
    },
    [askNewQuestion],
  );

  const onMomentPlayHandler = useCallback(
    (timecode: number) => {
      onMomentPlay(timecode);

      if (
        isEmbeded &&
        onClose &&
        overrideBreakpoint &&
        TO_CLOSE_BRAKE_POINTS.includes(overrideBreakpoint)
      ) {
        onClose();
      }
    },
    [isEmbeded, onClose, onMomentPlay, overrideBreakpoint],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && questionText.trim().length && !isComposing) {
        onAskCustomQuestion(questionText);
        sendAskAQuestionEvent(questionText);
      }
    },
    [onAskCustomQuestion, sendAskAQuestionEvent, questionText, isComposing],
  );

  useEffect(
    function handleQuestionsLoadingEnd() {
      if (
        activePanel === ViewerQnAPanels.LOADER &&
        !isActivatingAi &&
        !isQuestionsLoading &&
        !error &&
        questions?.length
      ) {
        // Too fast panel change with input's autoFocus set to true crashes the Drawer's slide animation when opening the module
        setTimeout(() => {
          setActivePanel(ViewerQnAPanels.QUESTIONS);
        }, 300);
      }
    },
    [activePanel, isActivatingAi, isQuestionsLoading, error, videoId, questions?.length],
  );

  useEffect(
    function handleLoadingStateChangeWhenNotInLoader() {
      if (questionResponseLoading) {
        setActivePanel(ViewerQnAPanels.ASK_QUESTION);
        sendViewGenerateAIAnswerEvent();
      } else if (isActivePanelAskQuestion && !questionResponseLoading) {
        setActivePanel(ViewerQnAPanels.ANSWER);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activePanel, questionResponseLoading, sendViewGenerateAIAnswerEvent],
  );

  useEffect(() => {
    if (contentWrapperRef?.current && activePanel === ViewerQnAPanels.ANSWER) {
      contentWrapperRef.current.scrollTo(0, 0);
    }
  }, [activePanel]);

  useEffect(
    function handleNoAnswerData() {
      if (activePanel === ViewerQnAPanels.ANSWER && !answer) {
        const errorMessage = `${finalCopy.failedToAskTheVimeoAi}. ${questionResponseError?.message}`;
        setActivePanel(ViewerQnAPanels.QUESTIONS);
        notify(errorMessage, 'warning', 5000);
      }
    },
    [activePanel, answer, questionResponseError, notify],
  );

  useEffect(
    function handleAiActivation() {
      if (isActivatingAi) {
        setActivePanel(ViewerQnAPanels.AI_ACTIVATION);
      } else {
        setActivePanel(ViewerQnAPanels.LOADER);
      }
    },
    [isActivatingAi],
  );

  // Displaying limit error on QnA activation
  useEffect(() => {
    (async function () {
      if (error && !isInterractionLimitErrorOnActivation) {
        try {
          const isInterractionLimitError = await isClipInterractionsLimitError(error);
          setIsInterractionLimitErrorOnActivation(isInterractionLimitError);
        } catch {
          setIsInterractionLimitErrorOnActivation(false);
        }
      }
    })();
  }, [error, isInterractionLimitErrorOnActivation]);

  // Displaying limit error instead of question input
  useEffect(() => {
    (async function () {
      if (questionResponseError && !isInterractionLimitErrorOnAskQuestion) {
        try {
          const isInterractionLimitError =
            await isClipInterractionsLimitError(questionResponseError);
          setIsInterractionLimitErrorOnAskQuestion(isInterractionLimitError);
        } catch {
          setIsInterractionLimitErrorOnAskQuestion(false);
        }
      }
    })();
  }, [isInterractionLimitErrorOnAskQuestion, questionResponseError]);

  useEffect(
    function notifyPlayerAfterStatusReceived() {
      if (isPlayer && hasValidStatus) {
        sendAppReady();
      }
    },
    [isPlayer, hasValidStatus],
  );

  if (!isActivatingAi && error) {
    if (isInterractionLimitErrorOnActivation) {
      return (
        <ResponsiveWrapper
          data-testid="viewer-qna"
          alignSelf="stretch"
          display="flex"
          flexDirection="column"
          aiThinking={isActivePanelAskQuestion && !isEmbeded}
          id="viewerqna"
          isPlayer={isPlayer}
        >
          <ViewerQnAHeader
            overrideBreakpoint={overrideBreakpoint}
            onClose={onClose}
            activePanel={activePanel}
            onBack={setActivePanel}
          />
          <ClipInterractionsLimitError iconSize="sm" />
        </ResponsiveWrapper>
      );
    }

    return <ErrorState error={error} onRetry={forceRefresh} />;
  }

  return (
    <ResponsiveWrapper
      data-testid="viewer-qna"
      alignSelf="stretch"
      display="flex"
      flexDirection="column"
      aiThinking={isActivePanelAskQuestion && !isEmbeded}
      id="viewerqna"
      isPlayer={isPlayer}
    >
      <ViewerQnAHeader
        overrideBreakpoint={overrideBreakpoint}
        onClose={onClose}
        activePanel={activePanel}
        onBack={setActivePanel}
      />
      <Box h="100%" overflowY="auto" ref={contentWrapperRef}>
        {activePanel === ViewerQnAPanels.LOADER && (
          <LoaderWrapper data-testid="viewer-qna-loader">
            <Spinner size="xl" />
          </LoaderWrapper>
        )}
        {activePanel === ViewerQnAPanels.QUESTIONS && (
          <QuestionsPanel
            onSelectLanguage={handleSelectLanguage}
            selectedLanguage={selectedLanguage}
            uniqueLanguages={uniqueLanguages}
            questions={questionsBySelectedLang}
            onAskPredefinedQuestion={onAskPredefinedQuestion}
            isEmbeded={isPlayer}
          />
        )}
        {isActivePanelAskQuestion && <AskQuestionPanel />}
        {activePanel === ViewerQnAPanels.AI_ACTIVATION && <AiActivationPanel />}
        {activePanel === ViewerQnAPanels.LOGIN_REQUIRED && (
          <LoginRequiredPanel onLoginRequired={onLoginRequired} />
        )}
        {activePanel === ViewerQnAPanels.ANSWER && (
          <Box
            height="100%"
            w="100%"
            sx={{
              '> :first-child': {
                width: '100%',
              },
            }}
          >
            <QuestionDisplay
              showPrompt={false}
              onMomentSelect={onMomentPlayHandler}
              onAskQuestion={onAskCustomQuestion}
            />
          </Box>
        )}
      </Box>
      <Box p="16px">
        {showQuestionInput && isInterractionLimitErrorOnAskQuestion ? (
          <Flex borderTop="1px solid" borderColor="stroke" pt="24px">
            <ClipInterractionsLimitError iconSize="xs" />
          </Flex>
        ) : null}
        {showQuestionInput && !isInterractionLimitErrorOnAskQuestion && (
          <>
            <InputGroup>
              <QuestionInput
                isLoading={isActivePanelAskQuestion}
                data-testid="custom-question-input"
                autoFocus
                isDisabled={isQuestionInputDisabled}
                maxLength={500}
                isPlayer={isPlayer}
                value={isQuestionInputDisabled ? question : questionText}
                placeholder={finalCopy.askAboutThisVideo}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setQuestionText(event.target.value);
                }}
                onCompositionEnd={() => setIsComposing(false)}
                onCompositionStart={() => setIsComposing(true)}
                onKeyDown={handleKeyDown}
              />
              <InputRightElement>
                <IconButton
                  aria-label="Submit question"
                  variant="tertiary"
                  isDisabled={isQuestionInputDisabled || questionText.trim().length === 0}
                  isLoading={isActivePanelAskQuestion}
                  icon={<ArrowUp />}
                  size="sm"
                  sx={{
                    ['>div>div']: {
                      width: '24px',
                      height: '24px',
                    },
                  }}
                  onClick={() => {
                    if (questionText.trim().length) {
                      onAskCustomQuestion(questionText);
                      sendAskAQuestionEvent(questionText);
                    }
                  }}
                />
              </InputRightElement>
            </InputGroup>
            <Box textAlign={'center'} color={'text-secondary'} fontSize={'text-xs'} pt={'16px'}>
              {activePanel === ViewerQnAPanels.QUESTIONS
                ? finalCopy.qnaTermAndConditions()
                : finalCopy.aiCanBeWrong}
            </Box>
          </>
        )}
      </Box>
    </ResponsiveWrapper>
  );
};
